var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect } from "react";
import { Button, Loading, MessageBanner, Modal, ModalFooter, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { invalidateAgendaViews } from "../../utils/agendaQueries";
import { useServerErrorMessage } from "../../hooks/useServerErrorMessage";
import css from "./Agenda.module.css";
var ERROR_FALLBACK_ID = "ui-br-legaldeposits.agenda.error.generic";
var IMPACT_HEADING_IDS = {
    library: "ui-br-legaldeposits.agenda.impact.heading",
    linkedPair: "ui-br-legaldeposits.agenda.impact.heading.linkedPair",
    allLibraries: "ui-br-legaldeposits.agenda.impact.heading.allLibraries",
};
var AgendaImpact = function (_a) {
    var impact = _a.impact, scope = _a.scope;
    return (React.createElement("div", { className: css.impact, "data-test-id": "agenda-impact" },
        React.createElement("strong", null,
            React.createElement(FormattedMessage, { id: IMPACT_HEADING_IDS[scope] })),
        React.createElement("ul", null,
            React.createElement("li", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.created", values: { count: impact.copiesCreated } })),
            React.createElement("li", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.removed", values: { count: impact.copiesRemoved } })),
            React.createElement("li", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.kept", values: { count: impact.copiesKept } })),
            React.createElement("li", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.total", values: { count: impact.copiesKept + impact.copiesCreated } }))),
        React.createElement("p", { className: css.hint },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.removedHint" })),
        impact.otherLibrariesCopiesCreated > 0 && (React.createElement("p", { "data-test-id": "agenda-impact-others" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.others", values: {
                    count: impact.otherLibrariesCopiesCreated,
                    libraries: impact.otherLibrariesAffected,
                } }),
            " ",
            React.createElement("span", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.othersHint" })))),
        scope === "linkedPair" && (React.createElement("p", { className: css.hint },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.movedHint" })))));
};
// The backend performs the change and rolls it back on dryRun, so the preview fails with exactly
// the error the real change would hit - that is the dialog's validation.
export var AgendaChangeModal = function (_a) {
    var open = _a.open, heading = _a.heading, previewKey = _a.previewKey, canSubmit = _a.canSubmit, run = _a.run, onClose = _a.onClose, onChanged = _a.onChanged, confirmLabel = _a.confirmLabel, _b = _a.impactScope, impactScope = _b === void 0 ? "library" : _b, children = _a.children;
    var formatMessage = useIntl().formatMessage;
    var queryClient = useQueryClient();
    var preview = useQuery(__spreadArray(["agenda-change-preview"], previewKey, true), function () { return run(true); }, {
        enabled: open && canSubmit,
        retry: false,
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });
    var changeMutation = useMutation(function (runChange) { return runChange(false); }, {
        onSuccess: function (result) {
            invalidateAgendaViews(queryClient);
            onChanged(result);
        },
    });
    var reset = changeMutation.reset;
    useEffect(function () {
        if (!open)
            reset();
    }, [open, reset]);
    var previewError = useServerErrorMessage(open && canSubmit ? preview.error : null, ERROR_FALLBACK_ID);
    var changeError = useServerErrorMessage(changeMutation.error, ERROR_FALLBACK_ID);
    var handleConfirm = useCallback(function () { return changeMutation.mutate(run); }, [changeMutation, run]);
    var confirmDisabled = !canSubmit ||
        preview.isFetching ||
        preview.isError ||
        changeMutation.isLoading;
    return (React.createElement(Modal, { open: open, label: heading, onClose: onClose, dismissible: true, size: "medium", footer: React.createElement(ModalFooter, null,
            React.createElement(Button, { buttonStyle: "primary", onClick: handleConfirm, disabled: confirmDisabled, "data-test-id": "agenda-change-confirm" }, confirmLabel !== null && confirmLabel !== void 0 ? confirmLabel : formatMessage({
                id: "ui-br-legaldeposits.agenda.dialog.confirm",
            })),
            React.createElement(Button, { onClick: onClose, disabled: changeMutation.isLoading },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.cancel" }))) },
        children,
        canSubmit && preview.isFetching && (React.createElement("p", { className: css.hint },
            React.createElement(Loading, null),
            " ",
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.impact.loading" }))),
        canSubmit && !preview.isFetching && preview.data && (React.createElement(AgendaImpact, { impact: preview.data, scope: impactScope })),
        previewError && (React.createElement(MessageBanner, { type: "error", "data-test-id": "agenda-preview-error" }, previewError)),
        changeError && (React.createElement(MessageBanner, { type: "error", "data-test-id": "agenda-change-error" }, changeError))));
};
